import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useGetCallFlowsQuery, useGetOwnPhoneNumbersQuery } from "@/store/rest_api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, History, Loader2 } from "lucide-react";
import { toast } from "sonner";
import FlowBuilder from "./FlowBuilder";
import RevisionsSheet from "./RevisionsSheet";
import { useRestoreCallFlowRevisionMutation } from "@/store/rest_api";
import { CallFlowRevision } from "@/model/interface/CallFlow";
import { apiErrorMessage } from "@/util/apiError";
import WaitAudioPicker from "@/components/audio/WaitAudioPicker";
import DialerTreeConfig from "@/app/config";
import { Node, Edge } from "@xyflow/react";
import { CallFlowType } from "@/model/interface/CallFlowType";
import { CallFlowActionRequest } from "@/model/interface/api/CallFlowActionRequest";
import { convertNodesToActions, serializeCanvas } from "@/util/flow";
import { missingRequiredFields } from "@/util/callFlowNodes";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import { UnsavedChangesDialog } from "./UnsavedChangesDialog";
import { ActiveFlowConflictDialog } from "./ActiveFlowConflictDialog";
import { useFeature } from "@/hooks/useFeature";
import { ActionNodeData } from "@/model/interface/CallFlow";

const callFlowSchema = z.object({
  name: z.string().min(1, "Flow name is required"),
  description: z.string().optional(),
  type: z.string().min(1),
  phoneNumberId: z.string().optional(),
  callerIdLookupMode: z.enum(["OFF", "UNKNOWN_ONLY", "ALWAYS"]),
  ringbackAudioClipId: z.string().nullable().optional(),
  holdAudioClipId: z.string().nullable().optional(),
  screenUnknownCallers: z.boolean(),
  active: z.boolean(),
});

export type CallFlowFormValues = z.infer<typeof callFlowSchema>;

export interface CallFlowPersistPayload {
  values: CallFlowFormValues;
  active: boolean;
  nodes: Node[];
  edges: Edge[];
  actions: CallFlowActionRequest[];
}

interface CallFlowEditorProps {
  title: string;
  subtitle: string;
  submitLabel: string;
  isSaving: boolean;
  initialValues?: CallFlowFormValues;
  initialNodes?: Node[];
  initialEdges?: Edge[];
  currentFlowId?: string;
  initialTab?: EditorTab;
  onActiveTabChange?: (tab: EditorTab) => void;
  onPersist: (payload: CallFlowPersistPayload) => Promise<boolean>;
  onRestored?: () => Promise<void>;
}

export type EditorTab = "details" | "flow";

export default function CallFlowEditor({
  title,
  subtitle,
  submitLabel,
  isSaving,
  initialValues,
  initialNodes,
  initialEdges,
  currentFlowId,
  initialTab,
  onActiveTabChange,
  onPersist,
  onRestored,
}: CallFlowEditorProps) {
  const navigate = useNavigate();
  const { data: phoneNumbers } = useGetOwnPhoneNumbersQuery();
  const { data: callFlows } = useGetCallFlowsQuery();
  const hasCallControls = useFeature("CALL_CONTROLS", { refetchOnMount: true });

  const [activeTab, setActiveTab] = useState<EditorTab>(initialTab ?? "details");
  const [flowNodes, setFlowNodes] = useState<Node[]>(initialNodes ?? []);
  const [flowEdges, setFlowEdges] = useState<Edge[]>(initialEdges ?? []);
  const [hasCanvasChanges, setHasCanvasChanges] = useState(false);
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
  const [isConflictDialogOpen, setIsConflictDialogOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState<CallFlowFormValues | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [pendingRestore, setPendingRestore] = useState<CallFlowRevision | null>(null);
  const [isApplyingRestore, setIsApplyingRestore] = useState(false);
  const [restoreRevision] = useRestoreCallFlowRevisionMutation();
  const canvasBaseline = useRef<string | null>(null);

  const form = useForm<CallFlowFormValues>({
    resolver: zodResolver(callFlowSchema),
    defaultValues: initialValues ?? {
      name: "",
      description: "",
      type: CallFlowType.BASIC,
      phoneNumberId: "",
      callerIdLookupMode: "OFF",
      ringbackAudioClipId: null,
      holdAudioClipId: null,
      screenUnknownCallers: false,
      active: true,
    },
  });

  const listRoute = "/manage-organisation/telephony?tab=call-flows";
  const hasUnsavedChanges = hasCanvasChanges || form.formState.isDirty;
  const advancedTypeDisabled = !hasCallControls && initialValues?.type !== CallFlowType.ADVANCED;

  useUnsavedChangesGuard(hasUnsavedChanges);

  const handleSaveFlow = (nodes: Node[], edges: Edge[]) => {
    setFlowNodes(nodes);
    setFlowEdges(edges);

    const serialized = serializeCanvas(nodes, edges);
    if (canvasBaseline.current === null) {
      canvasBaseline.current = serialized;
      return;
    }
    setHasCanvasChanges(serialized !== canvasBaseline.current);
  };

  const leaveEditor = () => {
    setIsDiscardDialogOpen(false);
    navigate(listRoute);
  };

  const requestLeaveEditor = () => {
    if (hasUnsavedChanges) {
      setIsDiscardDialogOpen(true);
      return;
    }
    leaveEditor();
  };

  const performRestore = async (revision: CallFlowRevision) => {
    if (!currentFlowId) {
      return;
    }
    setIsApplyingRestore(true);
    try {
      await restoreRevision({ id: currentFlowId, revisionId: revision.id }).unwrap();
      setHasCanvasChanges(false);
      setIsHistoryOpen(false);
      await onRestored?.();
      toast.success("Version restored");
    } catch (error) {
      setIsApplyingRestore(false);
      toast.error(apiErrorMessage(error, "Could not restore this version"));
    }
  };

  const requestRestore = (revision: CallFlowRevision) => {
    if (hasUnsavedChanges) {
      setPendingRestore(revision);
      return;
    }
    void performRestore(revision);
  };

  const handleTabChange = async (nextTab: string) => {
    if (nextTab === "flow" && !(await form.trigger())) {
      return;
    }
    setActiveTab(nextTab === "flow" ? "flow" : "details");
  };

  const phoneNumberLabelFor = (phoneNumberId?: string) => {
    const phoneNumber = phoneNumbers?.find((candidate) => candidate.id === phoneNumberId);
    return phoneNumber?.alias || phoneNumber?.number || "This number";
  };

  const conflictingFlowFor = (phoneNumberId?: string) => {
    if (!phoneNumberId || phoneNumberId === "-") {
      return undefined;
    }
    return callFlows?.find(
      (candidate) => candidate.id !== currentFlowId && candidate.active && candidate.phoneNumber?.id === phoneNumberId,
    );
  };

  const saveFlow = async (values: CallFlowFormValues, active: boolean) => {
    const saved = await onPersist({
      values,
      active,
      nodes: flowNodes,
      edges: flowEdges,
      actions: convertNodesToActions(flowNodes, flowEdges),
    });
    if (!saved) {
      return;
    }
    setHasCanvasChanges(false);
    form.reset(values);
    navigate(listRoute);
  };

  const onSubmit = async (values: CallFlowFormValues) => {
    const actions = convertNodesToActions(flowNodes, flowEdges);

    if (actions.length === 0) {
      setActiveTab("flow");
      toast.error("Please add at least one action to your flow");
      return;
    }

    if (flowNodes.some((node) => node.type === "picker")) {
      setActiveTab("flow");
      toast.error("Choose an action for every new step before saving");
      return;
    }

    const incompleteNodes = flowNodes.filter((node) => {
      if (node.type !== "action" && node.type !== "end") {
        return false;
      }
      const nodeData = node.data as ActionNodeData;
      return missingRequiredFields(nodeData.actionType, nodeData).length > 0;
    });
    if (incompleteNodes.length > 0) {
      const nodeNames = incompleteNodes.map((node) => {
        const nodeData = node.data as ActionNodeData;
        return nodeData.label || nodeData.actionType;
      });
      setActiveTab("flow");
      toast.error(`Complete the required fields on: ${nodeNames.join(", ")}`);
      return;
    }

    if (values.active && conflictingFlowFor(values.phoneNumberId)) {
      setPendingValues(values);
      setIsConflictDialogOpen(true);
      return;
    }

    await saveFlow(values, values.active);
  };

  const onInvalidSubmit = () => {
    setActiveTab("details");
  };

  useEffect(() => {
    onActiveTabChange?.(activeTab);
  }, [activeTab]);

  return (
    <div className="flex h-[calc(100dvh-4rem)] flex-col text-foreground">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, onInvalidSubmit)} className="flex min-h-0 flex-1 flex-col">
          <Tabs value={activeTab} onValueChange={handleTabChange} className="flex min-h-0 flex-1 flex-col">
            <div className="flex flex-wrap items-center gap-3 border-b px-4 py-3">
              <Button type="button" variant="ghost" size="icon" onClick={requestLeaveEditor} title="Back to call flows">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="min-w-0">
                <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
                <p className="text-sm text-muted-foreground">{subtitle}</p>
              </div>

              <TabsList className="ml-2">
                <TabsTrigger value="details">Basic Information</TabsTrigger>
                <TabsTrigger value="flow">Flow</TabsTrigger>
              </TabsList>

              <div className="ml-auto flex items-center gap-2">
                {currentFlowId && (
                  <Button type="button" variant="ghost" onClick={() => setIsHistoryOpen(true)}>
                    <History className="mr-2 h-4 w-4" />
                    History
                  </Button>
                )}
                <Button type="button" variant="outline" onClick={requestLeaveEditor}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSaving || isApplyingRestore}>
                  {(isSaving || isApplyingRestore) && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                  {submitLabel}
                </Button>
              </div>
            </div>

            <TabsContent value="details" forceMount className="mt-0 min-h-0 flex-1 space-y-6 overflow-y-auto p-4 data-[state=inactive]:hidden">
              <Card className="max-w-3xl">
                <CardContent className="space-y-4 pt-6">
                  <FormField control={form.control} name="name" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Flow Name *</FormLabel>
                      <FormControl><Input placeholder="e.g., Sales Line, Support Team" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />

                  <FormField control={form.control} name="description" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl><Textarea placeholder="Brief description of this call flow" rows={3} {...field} /></FormControl>
                    </FormItem>
                  )} />

                  <FormField control={form.control} name="type" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Call Flow Type</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl><SelectTrigger><SelectValue placeholder="Select a type" /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value={CallFlowType.BASIC}>Basic, simple direct dial</SelectItem>
                          <SelectItem value={CallFlowType.ADVANCED} disabled={advancedTypeDisabled}>
                            Advanced, conference based with hold and monitoring
                            {!hasCallControls && " (not included in your plan)"}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )} />

                  <FormField control={form.control} name="phoneNumberId" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Assign to Phone Number</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl><SelectTrigger><SelectValue placeholder="Select a phone number" /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="-">None</SelectItem>
                          {phoneNumbers?.map((number) => (
                            <SelectItem key={number.id} value={number.id}>
                              {number.alias || number.carrierPhoneNumberSid}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )} />

                  <FormField control={form.control} name="active" render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                      <div className="space-y-0.5 pr-4">
                        <FormLabel>Active</FormLabel>
                        <p className="text-sm text-muted-foreground">
                          Only an active flow answers calls. A number can have one active flow at a time.
                        </p>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )} />

                  <FormField control={form.control} name="callerIdLookupMode" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Look up the caller's name</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="OFF">Never</SelectItem>
                          <SelectItem value="UNKNOWN_ONLY">Only when the contact has no name</SelectItem>
                          <SelectItem value="ALWAYS">On every call</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-sm text-muted-foreground">
                        Your organization is charged $0.02 per inbound call that is looked up, so restricting it to
                        unknown callers keeps the cost to strangers only.
                      </p>
                    </FormItem>
                  )} />

                  <FormField control={form.control} name="ringbackAudioClipId" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Ringback</FormLabel>
                      <FormControl>
                        <WaitAudioPicker
                          value={field.value}
                          onChange={field.onChange}
                          defaultLabel="The standard ringing tone"
                          defaultAudioUrl={`${DialerTreeConfig.baseAudioEndpoint}/ringback.wav`}
                        />
                      </FormControl>
                      <p className="text-sm text-muted-foreground">
                        What the caller hears while your team is being rung.
                      </p>
                    </FormItem>
                  )} />

                  <FormField control={form.control} name="holdAudioClipId" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Hold music</FormLabel>
                      <FormControl>
                        <WaitAudioPicker
                          value={field.value}
                          onChange={field.onChange}
                          defaultLabel="The standard hold music"
                          defaultAudioUrl={`${DialerTreeConfig.baseAudioEndpoint}/hold-music.mp3`}
                        />
                      </FormControl>
                      <p className="text-sm text-muted-foreground">
                        What a caller who arrived through this flow hears when an agent puts them on hold.
                      </p>
                    </FormItem>
                  )} />

                  <FormField control={form.control} name="screenUnknownCallers" render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                      <div className="space-y-0.5 pr-4">
                        <FormLabel>Screen unknown callers</FormLabel>
                        <p className="text-sm text-muted-foreground">
                          Callers who are not a named contact yet must press 1 before the flow starts, so robocalls
                          never ring your team. Known contacts go straight through.
                        </p>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )} />
                </CardContent>
              </Card>

              <div className="flex max-w-3xl justify-end">
                <Button type="button" onClick={() => handleTabChange("flow")}>
                  Next
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="flow" className="mt-0 flex min-h-96 flex-1 flex-col">
              <FlowBuilder initialNodes={flowNodes} initialEdges={flowEdges} onSave={handleSaveFlow} />
            </TabsContent>
          </Tabs>
        </form>
      </Form>

      <UnsavedChangesDialog
        open={isDiscardDialogOpen}
        onOpenChange={setIsDiscardDialogOpen}
        description="This call flow has changes that have not been saved. Leaving now loses them."
        onDiscard={leaveEditor}
      />

      {currentFlowId && (
        <RevisionsSheet
          flowId={currentFlowId}
          open={isHistoryOpen}
          onOpenChange={setIsHistoryOpen}
          onRestore={requestRestore}
          isRestoring={isApplyingRestore}
        />
      )}

      <UnsavedChangesDialog
        open={pendingRestore !== null}
        onOpenChange={(isOpen) => !isOpen && setPendingRestore(null)}
        description="This call flow has changes that have not been saved. Restoring a version now loses them."
        onDiscard={() => {
          const revision = pendingRestore;
          setPendingRestore(null);
          if (revision) {
            void performRestore(revision);
          }
        }}
      />

      <ActiveFlowConflictDialog
        open={isConflictDialogOpen}
        onOpenChange={setIsConflictDialogOpen}
        conflictingFlowName={conflictingFlowFor(pendingValues?.phoneNumberId)?.name ?? "another flow"}
        phoneNumberLabel={phoneNumberLabelFor(pendingValues?.phoneNumberId)}
        onReplace={() => {
          setIsConflictDialogOpen(false);
          if (pendingValues) {
            saveFlow(pendingValues, true);
          }
        }}
        onSaveInactive={() => {
          setIsConflictDialogOpen(false);
          if (pendingValues) {
            saveFlow(pendingValues, false);
          }
        }}
      />
    </div>
  );
}
